import { Search, Check, X } from 'lucide-react';

// ── Status Badge ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const isActive = status === 'active' || status;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      isActive
        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
        : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
    }`}>
      {status.toString().toUpperCase()}
    </span>
  );
}

// ── Search Bar ────────────────────────────────────────────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Search…', inputClassName = '' }) {
  return (
    <div className="relative">
      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`h-10 pl-10 pr-3.5 py-0 bg-[#f4f6f8] dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1d57a5] w-full sm:w-80 text-slate-700 dark:text-slate-200 transition-all ${inputClassName}`}
      />
    </div>
  );
}

// ── Toast Notification ────────────────────────────────────────────────────────
export function Toast({ visible, message = 'Changes saved successfully!', success = true }) {
  if (!visible) return null;
  return (
    <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
          success ? "bg-[#36a852]" : "bg-[#d6040f]"
        }`}>
        {success ? <Check size={14} strokeWidth={3} /> : <X size={14} strokeWidth={3} />}
      </div>
      <span className="text-sm font-semibold">{message}</span>
    </div>
  );
}

// ── Empty / No-Results State ──────────────────────────────────────────────────
export function EmptyState({ query, label = 'results' }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Search size={32} className="text-slate-300 dark:text-slate-600 mb-4" />
      <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-1">No {label} found</p>
      {query && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          We couldn&apos;t find anything matching &ldquo;{query}&rdquo;
        </p>
      )}
    </div>
  );
}

// ── Page Header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <header className="mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">{title}</h1>
        <p className="text-slate-500 dark:text-slate-400">{subtitle}</p>
      </div>
      {actions && <div className="flex items-center gap-4">{actions}</div>}
    </header>
  );
}

// ── Step Stepper (Onboarding sidebar) ────────────────────────────────────────
export function StepStepper({ steps, currentStep, compact = false, collapsed = false }) {
  return (
    <div className="relative flex-1 hidden md:block">
      <div className="space-y-8 relative z-10">
        {steps.map((s, index) => {
          const isDone   = currentStep > s.step;
          const isActive = currentStep === s.step;
          const isLast   = index === steps.length - 1;

          return (
            <div
              key={s.step}
              className={`flex items-start relative ${
                collapsed ? "justify-centre gap-0" : "gap-4"
              }`}
            >

              {/* Vertical line — drawn downward from this circle, skipped on last step */}
              {!isLast && (
                <div
                  className="absolute top-10 w-0.5 bg-slate-200 dark:bg-slate-700 z-0"
                  style={{
                    left: "1.15rem",
                    height: "calc(2rem + 2px)",
                  }}
                />
              )}

              {/* Circle */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 flex-shrink-0 relative bg-white dark:bg-slate-800 z-10 ${
                isActive
                  ? 'border-[#1d57a5] text-[#1d57a5] shadow-md shadow-[#1d57a5]/20 dark:border-[#4a8ce3] dark:text-[#4a8ce3]'
                  : isDone
                    ? 'border-[#36a852] text-[#36a852] shadow-md shadow-[#36a852]/20 dark:border-[#4a8ce3] dark:text-[#4a8ce3]'
                    : 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500'
              }`}>
                {isDone ? <Check size={16} /> : s.step}
              </div>
              <div
                className={`${
                  compact || collapsed
                    ? "opacity-0 max-w-0 h-0 max-h-0 overflow-hidden pointer-events-none translate-x-[-4px] pt-0"
                    : "opacity-100 max-w-[220px] max-h-0 translate-x-0 pt-1"
                }`}
              >
                <h4 className={`text-sm font-bold ${
                  isActive ? 'text-[#1d57a5] dark:text-[#4a8ce3]'
                  : isDone  ? 'text-slate-800 dark:text-slate-200'
                  : 'text-slate-500 dark:text-slate-400'
                }`}>{s.label}</h4>
                <p className={`text-xs mt-0.5 ${
                  isActive ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'
                }`}>{s.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
